import React, { useState, useEffect, useMemo } from "react";
import TeamMemberLayout from "../TeamMemberLayout";
import UseGetData from "../../../../Components/Hooks/useFetchData";
import { Button, Select } from "antd";
import axiosInstance from "../../../../Services/axiosCommon";
import { Building, CalendarDays, MapPin, RefreshCw, User } from "lucide-react";
import NuDate from "../../../../Components/Utils/NuDate";

const { Option } = Select;

const months = [
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" },
];

function AvailabilityIndex() {
  const now = new Date();
  const [currentDate, setCurrentDate] = useState(now);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const { data: list } = UseGetData("", "", `/team/team-members`, null);

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee === selectedEmployee ? null : employee);
  };

  const years = Array.from(
    { length: 21 },
    (_, i) => currentDate.getFullYear() - 10 + i
  );

  const getStatusColor = (status) => {
    let Status = status.toLowerCase();
    switch (Status) {
      case "completed":
        return "bg-green-500";
      case "yet to start":
      case "new":
        return "bg-blue-500";
      case "pending":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusBorderColor = (status) => {
    let Status = status.toLowerCase();
    switch (Status) {
      case "completed":
        return "border-green-500";
      case "yet to start":
      case "new":
        return "border-blue-500";
      case "pending":
        return "border-red-500";
      default:
        return "border-gray-500";
    }
  };

  const getStatusTextColor = (status) => {
    let Status = status.toLowerCase();
    switch (Status) {
      case "completed":
        return "text-green-600";
      case "yet to start":
      case "new":
        return "text-blue-600";
      case "pending":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      setSelectedTask(null);
      setSelectedDate(null);
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      setSelectedTask(null);
      setSelectedDate(null);
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Previous month
    const prevMonthLastDate = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDate - i),
        currentMonth: false,
        task_count: 0,
        tasks: [],
      });
    }

    // Current month
    for (let i = 1; i <= daysInMonth; ++i) {
      const date = new Date(year, month, i);

      const matched = calendarData.filter((d) => {
        return (
          d.date.toISOString().split("T")[0] ===
          date.toISOString().split("T")[0]
        );
      });

      console.log("Matched Tasks for Date:",date, matched?.length, matched);

      const dayData = { task_count: matched?.length || 0, tasks: matched } || {
        task_count: 0,
        tasks: [],
      };

      days.push({ ...dayData, date, currentMonth: true });
    }

    // Next month (ensure 6 weeks)
    const totalCells = 42;
    const remainingCells = totalCells - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        currentMonth: false,
        task_count: 0,
        tasks: [],
      });
    }

    return days;
  }, [calendarData, currentDate]);

  const handleDateClick = (day) => {
    console.log("Selected Day:", day);

    setSelectedDate(day);
    if (day.currentMonth && day.task_count > 0) {
      setSelectedTask(day.tasks[0]);
    }
  };

  const [loading, setLoading] = useState(false);

  // Fixed date parser helper
  const parseDateSafely = (dateString) => {
    if (!dateString) return null;
    
    // Handle ISO dates (with timezone information)
    if (dateString.includes('T')) {
      return new Date(dateString);
    }
    
    // Handle date-only strings (YYYY-MM-DD)
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  // Update both fetch functions to use the safe parser:
  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const monthParam = currentDate.getMonth() + 1;
      const yearParam = currentDate.getFullYear();
      const response = await axiosInstance.get(
        `/calendar/events/${monthParam}/month/${yearParam}/year`
      );

      let Array = [];
      response.data?.tasks_by_date.forEach((item) => {
        let Value = item?.tasks || [];
        Value &&
          Value.forEach((task) => {
            let Temp = {
              id: task?.id || "",
              employeeId: task?.employeeId || task?.id,
              description: task?.description || "",
              priority: task?.priority || "",
              clientName: task?.clientName || " ",
              location: task?.location || " ",
              assignees: task?.assignees || [],
              // FIX: Use safe date parser
              date: parseDateSafely(task?.date) || null,
              status: task?.status || " ",
            };
            Array.push(Temp);
          });
      });

      setCalendarData(Array);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersCalendarData = async (userId) => {
    try {
      setLoading(true);
      const monthParam = currentDate.getMonth() + 1;
      const yearParam = currentDate.getFullYear();
      const response = await axiosInstance.get(
        `/calendar/events-by-inspectors/${userId}/${monthParam}/month/${yearParam}/year`
      );

      let Array = [];
      response.data?.tasks_by_date.forEach((item) => {
        let Value = item?.tasks || [];
        Value &&
          Value.forEach((task) => {
            let Temp = {
              id: task?.id || "",
              employeeId: task?.employeeId || task?.id,
              description: task?.description || "",
              priority: task?.priority || "",
              clientName: task?.clientName || " ",
              location: task?.location || " ",
              assignees: task?.assignees || [],
              // FIX: Use safe date parser
              date: parseDateSafely(task?.date) || null,
              status: task?.status || " ",
            };
            Array.push(Temp);
          });
      });

      setCalendarData(Array);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (selectedEmployee) {
      fetchUsersCalendarData(selectedEmployee.id);
    } else {
    fetchCalendarData();
    }
  }, [currentDate,selectedEmployee]);


  function Reset(){
    setSelectedEmployee(null);
    setSelectedTask(null);
    fetchCalendarData();
  }

  return (
    <TeamMemberLayout>
      <div className="flex flex-col md:flex-row gap-3 h-[calc(100vh-165px)]">
        {/* Employee List Section */}
        <div className="bg-white rounded-lg shadow w-full md:w-[30%] flex flex-col overflow-hidden h-[calc(100vh-84px)] md:h-[calc(100vh-165px)]">
          <div className="p-3 border-b flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-700">Team Members</h3>
            <p className="text-xs text-gray-500">
              {list.length || 0} Inspectors Available
            </p>
            </div>
                        <div>
              <button
                className="text-xs text-blue-500 hover:underline"
                onClick={Reset}
              >
                <RefreshCw />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto h-full p-3">
            {list.map((emp) => (
              <div
                key={emp?.id}
                className={`flex items-center gap-3 py-2 px-3 mb-1 rounded-xl border hover:bg-gray-50 cursor-pointer transition
                  ${
                    selectedEmployee?.id === emp.id
                      ? "bg-blue-50 border-blue-200"
                      : ""
                  }`}
                onClick={() => handleEmployeeClick(emp)}
              >
                {emp?.image ? (
                  <img
                    src={emp?.image}
                    alt={emp?.name}
                    className="w-9 h-9 rounded-full object-cover shadow"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold text-lg shadow">
                    {emp?.name?.charAt(0).toUpperCase()}
                  </div>
                )}

                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center gap-3">
 
  <span className="font-medium text-sm text-gray-800">
    {emp?.name}
  </span>
  <div className="flex gap-2">
   <div className="  px-1 py-0.5  bg-textColor-main text-white text-[10px]  rounded-lg flex items-center">
  {emp?.inspections?.availability
  ? emp.inspections.availability.charAt(0).toUpperCase() + emp.inspections.availability.slice(1).toLowerCase()
  : 0}

</div>

    
  
  <span className="text-xs font-medium text-gray-500">
    {emp?.inspections?.total_tasks || 0} tasks
  </span>
</div>

  
</div>


                  <div className="flex justify-between text-xs mt-1">
                    <span className={`${getStatusTextColor("Completed")}`}>
                      Completed : {emp?.inspections?.completed || 0}
                    </span>
                    <span className={`${getStatusTextColor("Yet to Start")}`}>
                      Yet to Start : {emp?.inspections?.yet_tot_start || 0}
                    </span>
                    <span className={`${getStatusTextColor("Pending")}`}>
                      Pending : {emp?.yet_tot_start?.pending || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar and Task Details Section */}
        <div className="w-full md:w-[70%]">
          <div className="flex flex-col md:flex-row gap-3 h-[calc(100vh-165px)]">
            {/* Calendar Section */}
            <div className="bg-white rounded-lg shadow w-full md:w-[60%] flex flex-col overflow-hidden h-[calc(100vh-84px)] md:h-[calc(100vh-165px)]">
              <div className="xl:sticky xl:top-0 z-10 rounded-t-lg bg-white p-4 lg:border-b  xl:flex justify-between items-center xl:h-14">
                <div>
                  <h3 className="text-gray-700 text-xs font-bold">
                    {selectedEmployee
                      ? `${selectedEmployee.name}'s Tasks`
                      : "Team Tasks Calendar"}
                  </h3>
                  <p className="text-gray-500 text-xs font-light">
                    {selectedEmployee
                      ? `Viewing tasks for ${selectedEmployee.name}`
                      : "Viewing all team tasks"}
                  </p>
                </div>
                <div className="flex gap-4 lg:gap-2 mt-2 xl:mt-0">
                  <Button
                    onClick={() => navigateMonth("prev")}
                    className="border-2 border-blue-500 text-blue-500 font-bold hover:bg-blue-50"
                  >
                    &lt;
                  </Button>

                  <div className="flex items-center gap-4">
                    <Select
                      value={currentDate.getMonth()}
                      style={{ width: 120 }}
                      onChange={(value) => {
                        const newDate = new Date(currentDate);
                        newDate.setMonth(value);
                        setCurrentDate(newDate);
                        setSelectedTask(null);
                        setSelectedDate(null);
                      }}
                    >
                      {months.map((m) => (
                        <Option key={m.value} value={m.value}>
                          {m.label}
                        </Option>
                      ))}
                    </Select>

                    <Select
                      value={currentDate.getFullYear()}
                      style={{ width: 100 }}
                      onChange={(value) => {
                        const newDate = new Date(currentDate);
                        newDate.setFullYear(value);
                        setCurrentDate(newDate);
                        setSelectedTask(null);
                        setSelectedDate(null);
                      }}
                    >
                      {years.map((y) => (
                        <Option key={y} value={y}>
                          {y}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <Button
                    onClick={() => navigateMonth("next")}
                    className="border-2 border-blue-500 text-blue-500 font-bold hover:bg-blue-50"
                  >
                    &gt;
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 lg:mt-0">
                <div className="grid grid-cols-7 w-full h-10 bg-gradient-to-r from-blue-600 to-blue-800 shadow-sm">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day, index) => (
                      <div
                        key={day}
                        className={`text-center font-medium text-white/90 py-2 flex items-center justify-center
                        ${index === 0 ? "rounded-l-lg" : ""} 
                        ${index === 6 ? "rounded-r-lg" : ""}`}
                      >
                        {day}
                      </div>
                    )
                  )}
                </div>
                <div className="grid grid-cols-7 gap-0 w-full">
                  {calendarDays.map((day) => {
                    const dayDate = new Date(day.date);
                    const dateString = getLocalDateString(dayDate);
                    const isToday = dateString === getLocalDateString(now);
                    const isSelected =
                      selectedDate?.date &&
                      dateString ===
                        getLocalDateString(new Date(selectedDate.date));
                    const hasTasks = day.task_count > 0;

                    const statusCounts = {};
                    if (day.tasks) {
                      day.tasks.forEach((task) => {
                        statusCounts[task.status] =
                          (statusCounts[task.status] || 0) + 1;
                      });
                    }

                    return (
                      <div
                        key={dateString}
                        onClick={() => handleDateClick(day)}
                        className={`p-1 border border-collapse h-[calc((100vh-273px)/6)] flex flex-col items-center justify-between
                      ${day.currentMonth ? "" : "bg-gray-50 text-gray-400"}
                      ${isToday ? "bg-yellow-100" : ""}
                      ${isSelected ? "bg-blue-50 border-blue-200" : ""}
                      cursor-pointer hover:bg-blue-50 `}
                      >
                        <div className="w-full text-right text-sm">
                          {dayDate.getDate()}
                        </div>
                        {hasTasks && (
                          <div className="flex flex-wrap justify-center gap-1">
                            {Object.entries(statusCounts).map(
                              ([status, count]) => (
                                <div
                                  key={status}
                                  className={`w-5 h-5 rounded-full ${getStatusColor(
                                    status
                                  )} text-white text-xs flex items-center justify-center`}
                                  title={`${status}: ${count}`}
                                >
                                  {count}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Task Details Section */}
            <div className="bg-white rounded-lg shadow w-full md:w-[40%] flex flex-col overflow-hidden h-[calc(100vh-84px)] md:h-[calc(100vh-165px)]">
              {/* Task Header */}
              <div className="w-full p-4 border-b bg-white sticky top-0 z-10">
                <p className="text-gray-700 text-sm font-bold">
                  {selectedDate?.date && selectedDate?.date
                    ? `Tasks for ${new Date(
                        selectedDate.date
                      ).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`
                    : "Select a date"}
                </p>
                {selectedEmployee && (
                  <p className="text-gray-500 text-xs mt-1">
                    Showing tasks for:{" "}
                    <span className="font-medium">{selectedEmployee.name}</span>
                  </p>
                )}
              </div>

                            {/* Scrollable Task List */}
                            <div className="p-4 space-y-4 overflow-y-auto">
                              {selectedDate?.tasks?.length > 0 ? (
                                selectedDate.tasks.map((task) => (
                                  <div
                                    key={task.id}
                                    className={`bg-white rounded-lg p-3 border shadow-sm transition
                                      ${
                                        selectedTask?.id === task.id
                                          ? "border-blue-500 shadow-md"
                                          : "border-gray-200 hover:border-blue-300"
                                      }
                                      ${getStatusColor(task.status)} bg-opacity-10`}
                                    onClick={() => setSelectedTask(task)}
                                  >
                                    <div className="flex justify-between items-start mb-2">
                                      <h3 className="text-sm font-semibold text-gray-800 line-clamp-2">
                                        {task.description || "No Description"}
                                      </h3>
                                      <span
                                        className={`text-xs font-medium px-2 py-0.5 rounded-lg capitalize ${
                                          task.priority === "high"
                                            ? "bg-red-100 text-red-600"
                                            : task.priority === "medium"
                                            ? "bg-yellow-100 text-yellow-600"
                                            : "bg-green-100 text-green-600"
                                        }`}
                                      >
                                        {task.priority || "None"}
                                      </span>
                                    </div>
              
                                    <div className="space-y-2 text-xs text-gray-700 mt-2">
                                      <div className="flex items-center gap-2">
                                        <Building
                                          size={12}
                                          className="text-gray-500 shrink-0"
                                        />
                                        <span>{task.clientName || "None"}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <MapPin
                                          size={12}
                                          className="text-gray-500 shrink-0"
                                        />
                                        <span>{task.location || "None"}</span>
                                      </div>
              
                                      <div className="flex items-start gap-2">
                                        <User
                                          size={12}
                                          className="text-gray-500 shrink-0 mt-0.5"
                                        />
                                        <span className="break-words">
                                          {task.assignees
                                            ?.map((i) => i.assigneeName)
                                            .join(", ") || "None"}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <CalendarDays
                                          size={12}
                                          className="text-gray-500 shrink-0"
                                        />
                                        <span>
                                          <NuDate value={task.date} format="DD-MMM-YYYY" />
                                        </span>
                                      </div>
                                    </div>
                                    <div className="mt-3 flex justify-between items-center">
                                      <div className="flex items-center overflow-hidden rounded-full text-[10px] 2xl:text-xs">
                                        <div
                                          className={`p-2 h-6 bg-white rounded-s-full text-xs flex items-center border ${getStatusBorderColor(
                                            task.status
                                          )}`}
                                        >
                                          Status
                                        </div>
                                        <div
                                          className={`p-2 h-6 ${getStatusColor(
                                            task.status
                                          )} text-white text-xs rounded-e-full flex items-center`}
                                        >
                                          {task.status || "-"}
                                        </div>
                                      </div>
              
                                      <div className="text-xs text-gray-500">
                                        {task.employeeId ? `ID: ${task.employeeId}` : ""}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4 text-center">
                                  {selectedDate
                                    ? selectedEmployee
                                      ? `${selectedEmployee.name} has no tasks on ${new Date(
                                          selectedDate.date
                                        ).toLocaleDateString()}`
                                      : "No tasks found for selected date"
                                    : "Select a date to view tasks"}
                                </div>
                              )}
                            </div>
            </div>
          </div>
        </div>
      </div>
    </TeamMemberLayout>
  );
}

export default AvailabilityIndex;
